import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { authAPI } from '../services/authAPI'
import { fetchTenantIdByEmail } from '../services/tenantService'
import { signInWithPopup, signInWithRedirect, getRedirectResult, OAuthProvider, getAuth, onAuthStateChanged } from 'firebase/auth'
import { initializeApp } from 'firebase/app'
import { firebaseConfig } from '../lib/firebaseConfig'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import { Eye, EyeOff, ArrowRight, Mail, Lock, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import MFAVerification from '../components/MFA/MFAVerification'
import AuthShell from '../components/Layout/AuthShell'
import { useHubBranding } from '../hooks/useHubBranding'

// Initialize Firebase app (matching POC approach exactly)
initializeApp(firebaseConfig)

const normalizeEmail = (email = '') => email.trim().toLowerCase()

export default function Login() {
  const { login, loading, error, completeMFALogin } = useAuth()
  const { branding } = useHubBranding()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoginMode, setIsLoginMode] = useState(true)
  const [step, setStep] = useState('email') // 'email', 'tenant-selection', or 'password'
  const [checkingSSO, setCheckingSSO] = useState(false)
  const [fetchingTenant, setFetchingTenant] = useState(false)
  const [tenantId, setTenantId] = useState(null)
  const [tenantAutoDetected, setTenantAutoDetected] = useState(false)
  const [availableTenants, setAvailableTenants] = useState([])
  const [ssoConfig, setSsoConfig] = useState(null)
  const [mfaSessionToken, setMfaSessionToken] = useState(null)
  const [requiresMFA, setRequiresMFA] = useState(false)
  const [loginError, setLoginError] = useState(null)
  const hasResetRef = React.useRef(false) // Track if we've already reset to prevent multiple resets

  // Reset login state when component mounts or user navigates to login page
  // This ensures clean state after logout - RUNS FIRST before other effects
  useEffect(() => {
    // Check if there's an active SSO attempt or MFA requirement (or "password first then link Azure" flow)
    const hasSSOAttempt = sessionStorage.getItem('sso_email') || sessionStorage.getItem('sso_tenant_id') ||
      (sessionStorage.getItem('link_azure_after_password') === 'true' && sessionStorage.getItem('link_azure_email'))
    const hasMFA = searchParams.get('mfa') === 'required'
    const hasBackendToken = localStorage.getItem('token')
    const logoutFlag = sessionStorage.getItem('logout_flag')
    const shouldReset = !!logoutFlag // Reset if logout flag exists
    
    // Safety check: If we're on password step but have no tenant/email, reset to email
    if (step === 'password' && !tenantId && !formData.email && !hasBackendToken && !hasSSOAttempt && !hasMFA) {
      console.log('[Login] Step is password but no tenant/email - forcing reset to email')
      setStep('email')
      setTenantId(null)
      setTenantAutoDetected(false)
      setAvailableTenants([])
      setSsoConfig(null)
      setCheckingSSO(false)
      setFetchingTenant(false)
      setLoginError(null)
      setFormData({ email: '', password: '' })
      return
    }
    
    // If reset parameter is present, or no active SSO attempt, no MFA, and no backend token, reset to email step
    // Also check if we've already reset to prevent multiple resets
    if ((shouldReset || (!hasSSOAttempt && !hasMFA && !hasBackendToken)) && !hasResetRef.current) {
      // Always reset form and state when coming to login page without active session
      // This handles logout scenarios and fresh visits
      console.log('[Login] Resetting to email step', { 
        pathname: location.pathname,
        shouldReset,
        hasSSOAttempt,
        hasMFA,
        hasBackendToken,
        currentStep: step
      })
      
      // Mark that we've reset
      hasResetRef.current = true
      
      // Reset all state IMMEDIATELY
      setStep('email')
      setTenantId(null)
      setTenantAutoDetected(false)
      setAvailableTenants([])
      setSsoConfig(null)
      setCheckingSSO(false)
      setFetchingTenant(false)
      setLoginError(null)
      
      // Clear form data to prevent auto-triggering or cached values
      setFormData({
        email: '',
        password: ''
      })
      
      // Clear logout flag after reset
      if (shouldReset) {
        sessionStorage.removeItem('logout_flag')
        console.log('[Login] Cleared logout flag after reset')
      }
    } else if (hasSSOAttempt || hasMFA || hasBackendToken) {
      // If we have an active session, allow reset ref to be used again
      hasResetRef.current = false
    }
  }, [location.pathname, searchParams, step, tenantId, formData.email]) // Include step and formData to detect invalid states

  // Check for redirect results and handle Firebase auth state on mount
  // Note: AuthContext also handles redirect results, so this is a fallback
  useEffect(() => {
    // Don't check redirect result if we're resetting (after logout)
    const shouldReset = searchParams.get('reset') === 'true'
    const hasSSOAttempt = sessionStorage.getItem('sso_email') || sessionStorage.getItem('sso_tenant_id')
    const hasBackendToken = localStorage.getItem('token')
    
    // Skip redirect check if we're resetting or have no active session
    if (shouldReset || (!hasSSOAttempt && !hasBackendToken)) {
      console.log('[Login] Skipping redirect result check - reset or no active session')
      return
    }
    
    const checkRedirectResult = async () => {
      try {
        const auth = getAuth()
        
        // Check if user is already signed in (AuthContext might have handled it)
        if (auth.currentUser) {
          console.log('[Login][Redirect] User already signed in, skipping redirect check (AuthContext handled it)')
          return
        }
        
        // IMPORTANT: Set tenant ID BEFORE calling getRedirectResult
        // The tenant ID must be set on the auth instance for multi-tenant redirects to work
        const storedTenantId = sessionStorage.getItem('sso_tenant_id')
        if (storedTenantId) {
          console.log('[Login][Redirect] Setting tenant ID on auth instance:', storedTenantId)
          auth.tenantId = storedTenantId
        } else {
          console.warn('[Login][Redirect] No tenant ID in sessionStorage - redirect may fail')
        }
        
        const result = await getRedirectResult(auth)
        
        if (result) {
          console.log('[Login][Redirect] Redirect result detected in Login component (fallback handler)', result.user?.email)
          
          // Get stored email from sessionStorage (might have been cleared by AuthContext)
          const storedEmail = sessionStorage.getItem('sso_email')
          const emailToUse = storedEmail || result.user?.email
          
          if (emailToUse) {
            try {
              const idToken = await result.user.getIdToken()
              const response = await authAPI.login({ email: normalizeEmail(emailToUse), idToken })
              
              if (response.data.requires_mfa && response.data.data?.session_token) {
                sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                sessionStorage.setItem('mfa_email', emailToUse)
                window.location.href = '/login?mfa=required'
                return
              }
              
              const { user, token } = response.data.data
              localStorage.setItem('token', token)
              console.log('[Login][Redirect] Backend login successful (fallback)')
              toast.success('SSO login successful!')
              window.location.href = '/dashboard'
            } catch (loginError) {
              console.error('[Login][Redirect] Backend login failed:', loginError)
              toast.error(loginError.response?.data?.message || 'SSO login failed')
              await auth.signOut()
            }
          }
        } else {
          // No redirect result from getRedirectResult (might have been consumed by AuthContext)
          // Check if we just came from a redirect by checking referrer
          const isFromFirebaseRedirect = document.referrer.includes('firebaseapp.com') || 
                                         document.referrer.includes('__/auth/handler')
          
          const currentUser = auth.currentUser
          const backendToken = localStorage.getItem('token')
          
          // If we came from Firebase redirect and have a Firebase user but no backend token,
          // AuthContext should be handling it. Give it a moment, then check again.
          if (isFromFirebaseRedirect && currentUser && !backendToken) {
            console.log('[Login] Detected Firebase redirect - waiting for AuthContext to complete login')
            // Wait a bit for AuthContext to handle it (AuthContext runs first)
            setTimeout(async () => {
              const tokenAfterWait = localStorage.getItem('token')
              const authAfterWait = getAuth()
              if (!tokenAfterWait && authAfterWait.currentUser) {
                // AuthContext didn't handle it, try to complete login here
                console.log('[Login] AuthContext didn\'t complete login, attempting fallback')
                try {
                  const email = authAfterWait.currentUser.email
                  if (email) {
                    const idToken = await authAfterWait.currentUser.getIdToken()
                    const response = await authAPI.login({ email: normalizeEmail(email), idToken })
                    
                    if (response.data.requires_mfa && response.data.data?.session_token) {
                      sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                      sessionStorage.setItem('mfa_email', email)
                      window.location.href = '/login?mfa=required'
                      return
                    }
                    
                    const { user, token } = response.data.data
                    localStorage.setItem('token', token)
                    console.log('[Login] Backend login completed successfully (fallback)')
                    toast.success('SSO login successful!')
                    window.location.href = '/dashboard'
                  }
                } catch (loginError) {
                  console.error('[Login] Failed to complete backend login:', loginError)
                  toast.error(loginError.response?.data?.message || 'Login failed')
                  await authAfterWait.signOut()
                }
              }
            }, 1000) // Wait 1 second for AuthContext
            return // Don't sign out, wait for AuthContext or fallback
          }
          
          // Only sign out if we're sure this isn't from a redirect
          // Check for SSO attempt markers or redirect indicators
          const hasSSOAttempt = sessionStorage.getItem('sso_email') || sessionStorage.getItem('sso_tenant_id')
          const urlParams = new URLSearchParams(window.location.search)
          const hasRedirectParams = urlParams.has('mfa') || urlParams.has('error')
          
          if (currentUser && !backendToken && !hasSSOAttempt && !hasRedirectParams && !isFromFirebaseRedirect) {
            // This likely means user was logged out - sign out from Firebase too
            console.log('[Login] Firebase user exists but no backend token and no SSO indicators - signing out from Firebase')
            await auth.signOut()
          }
        }
      } catch (error) {
        // Ignore 'auth/no-auth-event' - this is normal when there's no redirect pending
        if (error.code !== 'auth/no-auth-event') {
          console.error('[Login][Redirect] Error checking redirect result:', error)
        }
      }
    }
    
    checkRedirectResult()
    
    // Check for MFA requirement from URL params
    const mfaRequired = searchParams.get('mfa')
    if (mfaRequired === 'required') {
      const storedMfaToken = sessionStorage.getItem('mfa_session_token')
      const storedMfaEmail = sessionStorage.getItem('mfa_email')
      if (storedMfaToken && storedMfaEmail) {
        setMfaSessionToken(storedMfaToken)
        setRequiresMFA(true)
        setFormData(prev => ({ ...prev, email: storedMfaEmail }))
      }
    }
  }, [searchParams])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (loginError) {
      setLoginError(null)
    }
    
    // If email is cleared or changed, reset to email step (unless we're in password step with valid tenant)
    if (name === 'email' && step !== 'email') {
      const normalizedEmail = normalizeEmail(value)
      // Only reset if email is empty or if we're not in the middle of a valid flow
      if (!normalizedEmail || (!tenantId && step === 'password')) {
        console.log('[Login] Email cleared or changed - resetting to email step')
        setStep('email')
        setTenantId(null)
        setTenantAutoDetected(false)
        setAvailableTenants([])
        setSsoConfig(null)
      }
    }
  }

  const handleContinue = async (e) => {
    e.preventDefault()
    
    // Safety check: only proceed if we're on email step
    if (step !== 'email') {
      console.warn('[Login] handleContinue called but step is not email:', step, { stack: new Error().stack })
      return
    }
    
    // Prevent execution if we just reset (after logout)
    if (hasResetRef.current && !formData.email) {
      console.warn('[Login] handleContinue called but form is empty after reset - ignoring')
      return
    }
    
    const normalizedEmail = normalizeEmail(formData.email)

    if (!normalizedEmail) {
      toast.error('Please enter your email address')
      return
    }
    
    console.log('[Login] handleContinue called with email:', normalizedEmail, { currentStep: step })

    // Step 1: Lookup tenant first (like POC)
    setFetchingTenant(true)
    try {
      console.log('[Tenant][Lookup] Fetching tenant for email:', normalizedEmail)
      const tenantResult = await fetchTenantIdByEmail(normalizedEmail)
      
      console.log('Tenant lookup result:', tenantResult)
      
      // Handle multiple tenants case
      if (tenantResult.multipleTenants && tenantResult.tenants && tenantResult.tenants.length > 0) {
        console.log('✅ Multiple tenants found, showing selection UI')
        setAvailableTenants(tenantResult.tenants)
        setTenantAutoDetected(false)
        setStep('tenant-selection')
        return
      }
      
      // Handle single tenant found
      if (tenantResult.tenantId) {
        console.log('✅ Setting tenant ID:', tenantResult.tenantId)
        setTenantId(tenantResult.tenantId)
        setTenantAutoDetected(true)
        
        // Set tenant ID on Firebase auth instance
        const auth = getAuth()
        auth.tenantId = tenantResult.tenantId
        
        // Step 2: Check SSO availability with known tenant
        await checkSSOAndProceed(normalizedEmail, tenantResult.tenantId)
        return
      }
      
      // No tenant found or error
      console.log('⚠️ No tenant ID found in result')
      setTenantAutoDetected(false)
      if (tenantResult.error) {
        console.warn('Tenant lookup error:', tenantResult.error)
        // Still proceed to SSO check and password login
      }
      
      // Step 2: Check SSO availability (without tenant)
      await checkSSOAndProceed(normalizedEmail, null)
    } catch (error) {
      console.error('Error in tenant lookup:', error)
      // Still proceed to SSO check and password login
      await checkSSOAndProceed(normalizedEmail, null)
    } finally {
      setFetchingTenant(false)
    }
  }

  const checkSSOAndProceed = async (normalizedEmail, knownTenantId) => {
    // Prevent SSO check if we just reset (after logout)
    if (hasResetRef.current && step === 'email' && !formData.email) {
      console.log('[SSO][Check] Skipping SSO check - just reset after logout')
      return
    }
    
    console.log('[SSO][Check] Starting SSO check', { email: normalizedEmail, tenantId: knownTenantId, currentStep: step })
    setCheckingSSO(true)
    
    try {
      // Check SSO availability first to get the correct tenant_id
      console.log('[SSO][Check] Sending request', { email: normalizedEmail, tenantId: knownTenantId })
      const response = await authAPI.checkSSO(normalizedEmail)
      console.log('[SSO][Check] Response received', response.data)
      
      if (response.data && response.data.success) {
        const responseData = response.data.data || response.data
        const { hasSSO, ssoConfig: config, warning } = responseData
        
        // Log detailed SSO check result for debugging
        console.log('[SSO][Check] SSO check result:', { 
          hasSSO, 
          hasSSOType: typeof hasSSO,
          config, 
          configTenantId: config?.tenantId,
          warning 
        })

        // Get the tenant to use for Firebase checks (SSO config tenant takes priority)
        const tenantForFirebaseCheck = config?.tenantId || knownTenantId
        
        // 🔍 Check Firebase sign-in methods using the correct tenant (SSO config tenant if available)
        let hasAzureLinked = false
        let hasPasswordLinked = false
        try {
          const { fetchSignInMethodsForEmail, getAuth } = await import('firebase/auth')
          const auth = getAuth()
          if (tenantForFirebaseCheck) {
            auth.tenantId = tenantForFirebaseCheck
          }
          const firebaseMethods = await fetchSignInMethodsForEmail(auth, normalizedEmail)
          hasAzureLinked = firebaseMethods.includes('oidc.myazure')
          hasPasswordLinked = firebaseMethods.includes('password')
          
          console.log('[SSO][Check] 🔍 Firebase sign-in methods check:', {
            methods: firebaseMethods,
            hasAzureLinked,
            hasPasswordLinked,
            tenantId: auth.tenantId
          })
          
          if (hasAzureLinked) {
            console.log('[SSO][Check] ✅ Azure is ALREADY LINKED in Firebase!')
          } else {
            console.log('[SSO][Check] ⚠️ Azure is NOT linked in Firebase')
          }
        } catch (firebaseCheckError) {
          console.warn('[SSO][Check] Could not check Firebase methods:', firebaseCheckError.message)
        }

        // Show warning if provider exists in DB but not configured in Firebase
        if (warning) {
          toast.error(warning, { duration: 5000 })
        }

        // Strict check: SSO must be explicitly true (boolean), config must exist, and tenantId must be present
        // Convert string "true" to boolean if needed
        const hasSSOValue = hasSSO === true || hasSSO === 'true'
        const hasValidConfig = config && typeof config === 'object'
        const configTenantId = hasValidConfig ? (config.tenantId || '').trim() : ''
        const hasTenantId = configTenantId !== ''
        
        // IMPORTANT: SSO config tenant_id is the source of truth for SSO configuration
        // If SSO config has a tenant_id, use it (even if it doesn't match selected tenant)
        // The backend prioritizes SSO integrations by user's organization tenant_id, so this should match
        // But if there's a mismatch, trust the SSO config tenant since that's where SSO is actually configured
        let tenantMatches = true
        if (knownTenantId && configTenantId) {
          // Both tenant IDs exist - check if they match
          tenantMatches = configTenantId === knownTenantId
          if (!tenantMatches) {
            console.log('[SSO][Check] Tenant mismatch - SSO config tenant will be used', {
              selectedTenant: knownTenantId,
              ssoConfigTenant: configTenantId,
              note: 'Using SSO config tenant as it is the source of truth for SSO configuration'
            })
            // Still allow SSO to proceed - use SSO config tenant
          }
        } else if (configTenantId) {
          // SSO config has tenant_id but no tenant was selected - use SSO config tenant
          console.log('[SSO][Check] Using tenant from SSO config:', configTenantId)
        }
        
        console.log('[SSO][Check] Validation:', {
          hasSSOValue,
          hasValidConfig,
          hasTenantId,
          configTenantId,
          knownTenantId,
          tenantMatches,
          willProceedToSSO: hasSSOValue && hasValidConfig && hasTenantId
        })

        // If SSO is available AND properly configured (tenant matching is not required - SSO config tenant is used)
        if (hasSSOValue && hasValidConfig && hasTenantId) {
          const providerName = config.provider || ''
          const isAzureOrMicrosoft = providerName === 'azure' || providerName === 'microsoft'
          const isLatestProvider = config.isLatestProvider === true

          // When latest provider is Azure and user has only password (Azure not yet linked):
          // Show password field FIRST so user logs in with email/password, then we auto-link Azure after login.
          // Next time the same user logs in, Azure will be linked so we use Azure (latest provider) directly.
          if (isAzureOrMicrosoft && isLatestProvider && hasPasswordLinked && !hasAzureLinked) {
            console.log('[SSO][Check] Azure is latest provider but not yet linked - showing password first, then will auto-link after login')
            sessionStorage.setItem('sso_config', JSON.stringify({
              ...config,
              tenantId: configTenantId,
              provider: providerName,
              isLatestProvider: true
            }))
            sessionStorage.setItem('link_azure_after_password', 'true')
            sessionStorage.setItem('link_azure_email', normalizedEmail)
            sessionStorage.setItem('sso_tenant_id', configTenantId)
            if (configTenantId) {
              setTenantId(configTenantId)
              setTenantAutoDetected(true)
              const auth = getAuth()
              auth.tenantId = configTenantId
            }
            toast('Please sign in with your email and password first. Azure SSO will be linked automatically.', { duration: 6000 })
            setStep('password')
            return
          }

          console.log('[SSO][Check] ✅ SSO is configured for selected tenant - proceeding with SSO login')
          
          // 🔍 DEBUG: Double-check Firebase methods before proceeding
          try {
            const { fetchSignInMethodsForEmail, getAuth } = await import('firebase/auth')
            const auth = getAuth()
            const tenantToUse = configTenantId || knownTenantId
            if (tenantToUse) {
              auth.tenantId = tenantToUse
            }
            const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail)
            console.log('[SSO][Check] 🔍 Final Firebase methods check before SSO:', {
              methods,
              hasAzure: methods.includes('oidc.myazure'),
              tenantId: auth.tenantId
            })
          } catch (e) {
            console.warn('[SSO][Check] Could not verify Firebase methods:', e.message)
          }
          
          // Use tenant from SSO config (should match selected tenant)
          const tenantToUse = configTenantId || knownTenantId
          if (tenantToUse) {
            setTenantId(tenantToUse)
            setTenantAutoDetected(true)
            // Set tenant ID on Firebase auth instance
            const auth = getAuth()
            auth.tenantId = tenantToUse
          }
          await handleSSOLogin(config, normalizedEmail)
          return
        } else {
          // No SSO or SSO not properly configured, show password field
          const reason = !hasSSOValue ? 'hasSSO is false' 
                      : !hasValidConfig ? 'config missing' 
                      : !hasTenantId ? 'tenantId missing'
                      : 'unknown'
          console.log('[SSO][Check] ❌ No SSO available or not properly configured - showing password field', { 
            reason,
            hasSSOValue,
            hasValidConfig,
            hasTenantId,
            configTenantId,
            knownTenantId
          })
          console.log('[Login] Setting step to password (from checkSSOAndProceed - no SSO)', { stack: new Error().stack })
          setStep('password')
        }
      } else {
        // Response indicates failure but still allow password login
        console.warn('[SSO][Check] SSO check returned unsuccessful:', response.data.message)
        console.log('[Login] Setting step to password (from checkSSOAndProceed - unsuccessful response)', { stack: new Error().stack })
        setStep('password')
      }
    } catch (error) {
      console.error('[SSO][Check] Error during check', error)
      const message = error.response?.data?.message || error.message || 'Failed to check SSO availability'
      
      // For any error, fallback to password login
      if (error.response?.status === 404) {
        // User not found - allow password login attempt
        console.log('[Login] Setting step to password (from checkSSOAndProceed - 404 error)', { stack: new Error().stack })
        setStep('password')
      } else if (error.response?.status === 403) {
        // Account not active - show error
        toast.error(message)
      } else {
        // Other errors - log but allow password login as fallback
        console.error('SSO check failed, falling back to password login:', message)
        console.log('[Login] Setting step to password (from checkSSOAndProceed - other error)', { stack: new Error().stack })
        setStep('password')
      }
    } finally {
      setCheckingSSO(false)
    }
  }

  const handleTenantSelect = async (selectedTenantId) => {
    console.log('Tenant selected:', selectedTenantId)
    setTenantId(selectedTenantId || null)
    if (selectedTenantId) {
      setTenantAutoDetected(true)
      // Set tenant ID on Firebase auth instance
      const auth = getAuth()
      auth.tenantId = selectedTenantId
    }
    
    const normalizedEmail = normalizeEmail(formData.email)
    // After tenant selection, check SSO and proceed
    await checkSSOAndProceed(normalizedEmail, selectedTenantId)
  }

  const handleSSOLogin = async (config, normalizedEmailOverride) => {
    try {
      // Validate config before proceeding
      if (!config || typeof config !== 'object') {
        console.error('[SSO][Login] Invalid SSO config provided:', config)
        toast.error('SSO configuration is invalid. Please use password login.')
        setStep('password')
        return
      }

      const { provider: providerName, tenantId, isLatestProvider } = config
      console.log('[SSO][Login] Starting SSO login with config:', { tenantId, provider: providerName, isLatestProvider })

      if (!tenantId || tenantId.trim() === '') {
        console.error('[SSO][Login] No tenant ID in SSO config')
        toast.error('No tenant found for this email domain. Please use password login.')
        setStep('password')
        return
      }

      if (!providerName) {
        console.error('[SSO][Login] No provider name in SSO config')
        toast.error('SSO provider is not configured. Please use password login.')
        setStep('password')
        return
      }

      // Get auth instance (EXACTLY matching POC: getAuth() without parameters)
      const auth = getAuth()
      
      // Set tenant ID on auth instance (EXACTLY matching POC)
      auth.tenantId = tenantId

      // Map provider to Firebase provider ID (matching backend firebaseService.js)
      const providerMap = {
        'google': 'oidc.mygoogle',
        'microsoft': 'oidc.myazure',
        'azure': 'oidc.myazure',
        'okta': 'oidc.okta',
        'auth0': 'oidc.auth0',
        'oidc': 'oidc.generic',
        // Additional OIDC providers that use generic OIDC
        'onelogin': 'oidc.generic',
        'pingidentity': 'oidc.generic',
        'adfs': 'oidc.generic',
        'keycloak': 'oidc.generic',
        'jumpcloud': 'oidc.generic',
        'duo': 'oidc.generic',
        'sailpoint': 'oidc.generic'
      }
      
      const providerId = providerMap[providerName] || 'oidc.generic'
      console.log(`[SSO][Login] Mapping provider: ${providerName} -> ${providerId}`)
      
      // Get email early so it can be used for login_hint parameter
      const emailToUse = normalizedEmailOverride || normalizeEmail(formData.email)
      
      const provider = new OAuthProvider(providerId)
      
      // Force account selection for Azure/Microsoft providers to prevent auto-login
      // This ensures users see the account selection screen even if already signed in
      if (providerName === 'microsoft' || providerName === 'azure') {
        const customParams = {
          prompt: 'select_account' // Force account selection screen
        }
        
        // Add login_hint if email is available to pre-fill the email field
        // This improves UX while still showing account selection
        if (emailToUse) {
          customParams.login_hint = emailToUse
        }
        
        provider.setCustomParameters(customParams)
        console.log('[SSO][Login] Set custom parameters for Azure/Microsoft provider', customParams)
      }

      // Log redirect URIs that are registered in OAuth Console
      // These URIs must match what's registered in Google Cloud Console / Azure Portal
      // IMPORTANT: Use the exact auth domain that matches the registered redirect URI
      // Registered URI: https://esg-playbook-dev-415505.firebaseapp.com/__/auth/handler
      const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'esg-playbook-dev-415505.firebaseapp.com' // Force correct domain to match registered URI
      const redirectUri = `https://${authDomain}/__/auth/handler`
      const localRedirectUri = 'http://localhost:3000/api/auth/callback/google'
      
      // Debug: Check what Firebase config actually has
      console.log('Firebase Config authDomain:', firebaseConfig.authDomain)
      console.log('Using authDomain:', authDomain)
      console.log('Registered Redirect URIs (must match OAuth console):')
      console.log('Production (Firebase):', redirectUri)
      console.log('Local (API Callback):', localRedirectUri)
      console.log('Current origin:', window.location.origin)
      console.log('Tenant ID:', tenantId)
      console.log('Provider:', providerName)
      
      // IMPORTANT: Update the auth instance to use the correct auth domain
      // Firebase uses the auth domain from the config, so we need to ensure it matches
      if (auth.app && auth.app.options && auth.app.options.authDomain !== authDomain) {
        console.warn('⚠️ Auth domain mismatch detected!')
        console.warn('Config has:', auth.app.options.authDomain)
        console.warn('Required:', authDomain)
        console.warn('This may cause redirect URI errors!')
      }

      // Store email, tenant ID, and SSO config in sessionStorage so AuthContext can still handle redirects if needed
      sessionStorage.setItem('sso_email', emailToUse)
      sessionStorage.setItem('sso_tenant_id', tenantId)
      sessionStorage.setItem('sso_timestamp', Date.now().toString()) // Track when SSO started
      
      // Check if user is already logged in (has backend token) - this means they were logged in before SSO redirect
      // We'll use this to link Azure after password login if needed
      const hasBackendToken = localStorage.getItem('token')
      if (hasBackendToken) {
        sessionStorage.setItem('was_logged_in_before_sso', 'true')
        console.log('[SSO][Login] User was logged in before SSO redirect - will link Azure if account-exists error occurs')
      }
      // Persist SSO config so redirect handler can prioritize Azure/Microsoft
      // Only prioritize if this is the latest provider (by creation date)
      // Azure/Microsoft are NOT automatically prioritized - only if they're the latest
      const shouldPrioritizeProvider = isLatestProvider === true
      sessionStorage.setItem('sso_config', JSON.stringify({
        provider: providerName,
        isLatestProvider: shouldPrioritizeProvider
      }))
      
      // For Azure/Microsoft, try popup first (more reliable for multi-tenant)
      // If popup fails, fallback to redirect
      if (providerName === 'azure' || providerName === 'microsoft') {
        console.log('[SSO][Login] Attempting popup flow for Azure/Microsoft (more reliable for multi-tenant)', { tenantId, provider: providerName, email: emailToUse })
        
        try {
          // Try popup first - it's more reliable for multi-tenant scenarios
          const result = await signInWithPopup(auth, provider)
          console.log('[SSO][Login][Popup] Result received', result.user?.email)
          
          const firebaseEmail = result.user?.email ? normalizeEmail(result.user.email) : null
          const backendEmail = firebaseEmail || emailToUse
          
          // Get Firebase ID token
          const idToken = await result.user.getIdToken()
          
          console.log('[SSO][Login][Popup] Completing login with backend', { email: backendEmail })
          
          try {
            const response = await authAPI.login({ email: backendEmail, idToken })
            
            // Check if MFA is required
            if (response.data.requires_mfa && response.data.data?.session_token) {
              console.log('[SSO][Login][Popup] MFA verification required')
              sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
              sessionStorage.setItem('mfa_email', backendEmail)
              window.location.href = '/login?mfa=required'
              return
            }
            
            const { user, token } = response.data.data
            
            // Store token so AuthContext can pick it up on next load
            localStorage.setItem('token', token)
            
            // Set flag to indicate popup login completed - prevents AuthContext from processing redirect
            localStorage.setItem('sso_popup_completed', Date.now().toString())
            
            // Clear SSO markers since popup login is complete
            sessionStorage.removeItem('sso_email')
            sessionStorage.removeItem('sso_tenant_id')
            sessionStorage.removeItem('sso_config')
            sessionStorage.removeItem('sso_timestamp')
            localStorage.removeItem('sso_redirect_tenant_id')
            localStorage.removeItem('sso_redirect_email')
            localStorage.removeItem('sso_redirect_timestamp')
            localStorage.removeItem('sso_redirect_config')
            
            console.log('[SSO][Login][Popup] Backend login successful', { email: backendEmail })
            toast.success('SSO login successful with Azure!')
            
            // Redirect to dashboard
            window.location.href = '/dashboard'
            return
          } catch (loginError) {
            console.error('[SSO][Login][Popup] Backend login failed:', loginError)
            toast.error(loginError.response?.data?.message || 'SSO login failed')
            await auth.signOut()
            return
          }
        } catch (popupError) {
          // If popup fails (blocked or user cancelled), fallback to redirect
          console.warn('[SSO][Login] Popup failed, falling back to redirect:', popupError.message)
          console.log('[SSO][Login] Using redirect flow for Azure/Microsoft (popup fallback)', { tenantId, provider: providerName, email: emailToUse })
          
          // CRITICAL: Store email, tenant ID, and SSO config in sessionStorage BEFORE redirect
          // Use a fresh timestamp to prevent App.jsx from clearing it too early
          const redirectTimestamp = Date.now().toString()
          sessionStorage.setItem('sso_email', emailToUse)
          sessionStorage.setItem('sso_tenant_id', tenantId)
          sessionStorage.setItem('sso_timestamp', redirectTimestamp)
          sessionStorage.setItem('sso_config', JSON.stringify({
            provider: providerName,
            isLatestProvider: (isLatestProvider === true) || providerName === 'azure' || providerName === 'microsoft'
          }))
          
          // Also store in localStorage as backup (survives redirects better)
          localStorage.setItem('sso_redirect_tenant_id', tenantId)
          localStorage.setItem('sso_redirect_email', emailToUse)
          localStorage.setItem('sso_redirect_timestamp', redirectTimestamp)
          localStorage.setItem('sso_redirect_config', JSON.stringify({
            provider: providerName,
            isLatestProvider: (isLatestProvider === true) || providerName === 'azure' || providerName === 'microsoft'
          }))
          
          console.log('[SSO][Login] Stored SSO markers before redirect:', {
            tenantId,
            email: emailToUse,
            timestamp: redirectTimestamp,
            storedInSessionStorage: true,
            storedInLocalStorage: true
          })
          
          await signInWithRedirect(auth, provider)
          return // Redirect will be handled by AuthContext
        }
      }
      
      // Use popup for other providers
      // Account-exists errors will be handled in popup flow
      console.log('[SSO][Login] Attempting signInWithPopup', { tenantId, provider: providerName, email: emailToUse })
      
      try {
        // Prefer popup-based SSO to avoid full-page redirect issues (for non-Azure providers)
        const result = await signInWithPopup(auth, provider)
        console.log('[SSO][Login][Popup] Result received', result.user?.email)
        
        const firebaseEmail = result.user?.email ? normalizeEmail(result.user.email) : null
        const backendEmail = firebaseEmail || emailToUse
        
        // Get Firebase ID token
        const idToken = await result.user.getIdToken()
        
        console.log('[SSO][Login][Popup] Completing login with backend', { email: backendEmail })
        
        try {
          const response = await authAPI.login({ email: backendEmail, idToken })
          
          // Check if MFA is required (match AuthContext SSO flow)
          if (response.data.requires_mfa && response.data.data?.session_token) {
            console.log('[SSO][Login][Popup] MFA verification required')
            sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
            sessionStorage.setItem('mfa_email', backendEmail)
            window.location.href = '/login?mfa=required'
            return
          }
          
          const { user, token } = response.data.data
          
          // Store token so AuthContext can pick it up on next load
          localStorage.setItem('token', token)
          console.log('[SSO][Login][Popup] Backend login successful', { email: backendEmail })
          toast.success('SSO login successful with Azure!')
          
          // Navigate to dashboard (AuthProvider will hydrate user from token)
          window.location.href = '/dashboard'
          return
        } catch (loginError) {
          console.error('[SSO][Login][Popup] Backend login failed:', loginError)
          toast.error(loginError.response?.data?.message || 'SSO login failed')
          // Sign out from Firebase to avoid inconsistent state
          await auth.signOut()
          return
        }
      } catch (popupError) {
        console.warn('[SSO][Login][Popup] Popup sign-in failed:', popupError)
        
        // Handle account-exists-with-different-credential / provider conflict in popup flow
        if (popupError.code === 'auth/account-exists-with-different-credential' ||
            popupError.code === 'auth/credential-already-in-use' ||
            popupError.message?.includes('existing provider') ||
            popupError.message?.includes('already associated')) {
          
          console.log('[SSO][Login][Popup] Account exists with different credential - attempting automatic linking')
          
          try {
            const email = popupError.customData?.email || popupError.email || emailToUse
            const azureCredential = popupError.credential
            
            console.log('[SSO][Login][Popup] Account exists error details:', {
              email,
              hasCredential: !!azureCredential,
              providerName,
              errorCode: popupError.code,
              errorMessage: popupError.message
            })
            
            if (!email) {
              console.error('[SSO][Login][Popup] No email found in error')
              toast.error('Unable to identify account. Please try again.')
              return
            }
            
            if (!azureCredential) {
              console.error('[SSO][Login][Popup] No credential found in error - cannot link')
              toast.error('Unable to link Azure account. Please try signing in with your existing method first.')
              return
            }
            
            // Fetch existing sign-in methods
            const { fetchSignInMethodsForEmail, linkWithCredential, signInWithCredential, EmailAuthProvider } = await import('firebase/auth')
            const signInMethods = await fetchSignInMethodsForEmail(auth, email)
            console.log('[SSO][Login][Popup] Existing sign-in methods:', signInMethods)
            
            if (!signInMethods || signInMethods.length === 0) {
              console.error('[SSO][Login][Popup] No existing sign-in methods found')
              toast.error('Unable to determine existing sign-in method. Please try again.')
              return
            }
            
            const existingProviderId = signInMethods[0]
            console.log('[SSO][Login][Popup] Attempting to sign in with existing provider:', existingProviderId)
            
            // Map Firebase provider IDs to friendly names
            const providerNameMap = {
              'password': 'Email/Password',
              'google.com': 'Google',
              'facebook.com': 'Facebook',
              'twitter.com': 'Twitter',
              'github.com': 'GitHub',
              'oidc.mygoogle': 'Google',
              'oidc.myazure': 'Microsoft/Azure',
              'oidc.okta': 'Okta',
              'oidc.auth0': 'Auth0',
              'oidc.generic': 'SSO Provider'
            }
            
            const existingProviderName = providerNameMap[existingProviderId] || existingProviderId
            
            // If existing provider is password, we can't automatically sign in (need password)
            if (existingProviderId === 'password') {
              console.log('[SSO][Login][Popup] Existing provider is password - cannot auto-link without password')
              toast.error(
                `Your account exists with Email/Password. Please sign in with your password first, then Azure SSO will be linked automatically.`,
                { duration: 8000 }
              )
              // Show password field so user can sign in
              setStep('password')
              return
            }
            
            // For OAuth providers, we can't automatically sign in without user interaction
            // Show helpful message and redirect to use existing provider
            console.log('[SSO][Login][Popup] Existing provider is OAuth - cannot auto-link without user sign-in')
            toast.error(
              `Your account exists with ${existingProviderName}. Please sign in with ${existingProviderName} first, then Azure SSO will be linked automatically.`,
              { duration: 8000 }
            )
            return
            
          } catch (error) {
            console.error('[SSO][Login][Popup] Error handling account-exists:', error)
            toast.error('Unable to link Azure account. Please sign in with your existing method first.')
            return
          }
        }
        
        // For other popup errors (blocked, closed, etc.)
        console.warn('[SSO][Login][Popup] Popup sign-in failed:', popupError)
        
        // Show user-friendly error messages
        if (popupError.code === 'auth/popup-blocked') {
          toast.error('Popup was blocked by browser. Please allow popups and try again.')
        } else if (popupError.code === 'auth/popup-closed-by-user') {
          toast.error('Login was cancelled. Please try again.')
        } else {
          toast.error(popupError.message || 'SSO login failed. Please try again.')
        }
        
        // Don't fall back to redirect - let user try again
        return
      }

    } catch (error) {
      console.error('[SSO][Login] Error before redirect', error)
      // Provide more helpful error messages
      let errorMessage = 'SSO login failed'
      const errCode = error.code || ''
      const errMessage = error.message || ''
      const authDomain = firebaseConfig.authDomain || 'YOUR_AUTH_DOMAIN'
      
      // Check for Azure AD response type errors
      if (
        errMessage.includes('AADSTS700054') ||
        errMessage.includes('response_type') ||
        errMessage.includes('id_token') ||
        errMessage.includes('unsupported_response_type')
      ) {
        console.error('❌ Azure AD Response Type Configuration Error')
        console.error('Error:', errMessage)
        
        errorMessage = `Azure AD Configuration Error\n\n` +
          `❌ ID tokens are NOT enabled in your Azure AD app registration.\n\n` +
          `Firebase requires ID tokens to be enabled in Azure AD, even when using Authorization Code flow.\n\n` +
          `🔧 REQUIRED FIX - Enable ID Tokens in Azure AD:\n\n` +
          `1. Go to Azure Portal: https://portal.azure.com\n` +
          `2. Navigate to: Azure Active Directory → App registrations\n` +
          `3. Find and select your app (search by Client ID from SSO config)\n` +
          `4. Click "Authentication" in the left menu\n` +
          `5. Scroll to "Implicit grant and hybrid flows"\n` +
          `6. ✅ CHECK: "ID tokens (used for implicit and hybrid flows)"\n` +
          `7. Under "Redirect URIs", ensure this URI is added:\n` +
          `   https://${authDomain}/__/auth/handler\n` +
          `8. Click "Save" at the top\n` +
          `9. Wait 2-5 minutes for changes to propagate\n\n` +
          `⚠️ IMPORTANT: ID tokens MUST be enabled - this is required by Firebase.\n` +
          `Even though we use Authorization Code flow, Firebase still needs ID tokens enabled.\n\n` +
          `After enabling ID tokens, try logging in again.`
      } else if (
        errCode === 'auth/redirect-uri-mismatch' || 
        errCode === 'auth/invalid-continue-uri' ||
        errMessage.includes('redirect_uri_mismatch') ||
        errMessage.includes('redirect URI') ||
        errMessage.includes('invalid_request') ||
        errMessage.includes('invalid-continue-uri') ||
        errMessage.includes('continue_uri')
      ) {
        // Use the correct auth domain that matches registered redirect URI
        const authDomain = 'esg-playbook-dev-415505.firebaseapp.com'
        const redirectUri = `https://${authDomain}/__/auth/handler`
        const localRedirectUri = 'http://localhost:3000/api/auth/callback/google'
        
        // Check what Firebase is actually using
        const actualAuthDomain = firebaseConfig.authDomain || (auth.app?.options?.authDomain)
        
        console.error('❌ Redirect/Continue URI Error')
        console.error('Firebase Config authDomain:', actualAuthDomain)
        console.error('Required authDomain:', authDomain)
        console.error('Registered Redirect URIs (must match OAuth console):')
        console.error(`  • ${redirectUri}`)
        console.error(`  • ${localRedirectUri}`)
        
        if (actualAuthDomain !== authDomain) {
          console.error('⚠️ MISMATCH DETECTED!')
          console.error(`Firebase is using: ${actualAuthDomain}`)
          console.error(`But registered URI uses: ${authDomain}`)
          console.error('SOLUTION: Restart your dev server and clear browser cache!')
        }
        
        errorMessage = `Redirect URI Configuration Error\n\n` +
          `The Firebase auth domain must match the registered redirect URI.\n\n` +
          `Required Redirect URI:\n` +
          `• ${redirectUri}\n\n` +
          `Current Firebase auth domain: ${actualAuthDomain || 'unknown'}\n` +
          `Required auth domain: ${authDomain}\n\n` +
          `If domains don't match:\n` +
          `1. Restart your development server\n` +
          `2. Clear browser cache and hard refresh (Ctrl+Shift+R)\n` +
          `3. Verify ${redirectUri} is registered in Google Cloud Console\n` +
          `4. Wait a few minutes for changes to propagate\n` +
          `5. Try again`
      } else if (
        errCode === 'auth/account-exists-with-different-credential' ||
        errMessage.includes('existing provider') ||
        errMessage.includes('already associated') ||
        errMessage.includes('account exists')
      ) {
        // This error will be handled in the redirect result handler
        // Only latest provider will be prioritized and linked
        console.log('[SSO][Login] Account exists with different credential - latest provider will be linked in redirect result')
        errorMessage = 'Account exists with different sign-in method. Linking latest provider...'
        toast(errorMessage, { duration: 4000 })
        // Don't fall back to password - let the redirect complete and handle it in AuthContext
        return
      } else if (errMessage) {
        errorMessage = `Login failed: ${errMessage}\n\nError code: ${errCode || 'N/A'}`
      }
      
      toast.error(errorMessage)
      
      // Fallback to password login
      setStep('password')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoginError(null)
    
    try {
      if (isLoginMode) {
        const normalizedEmail = normalizeEmail(formData.email)
        console.log('[Login][Password] Attempting backend login', { email: normalizedEmail, tenantId })
        
        // Ensure tenant ID is set on Firebase auth instance if detected
        if (tenantId) {
          const auth = getAuth()
          auth.tenantId = tenantId
          console.log('[Login][Password] Tenant ID set on auth instance:', tenantId)
        }
        
        const response = await authAPI.login({
          ...formData,
          email: normalizedEmail
        })
        
        // Check if MFA is required
        if (response.data.requires_mfa && response.data.data?.session_token) {
          setMfaSessionToken(response.data.data.session_token)
          setRequiresMFA(true)
          console.log('[Login][MFA] MFA verification required')
          return
        }
        
        // Normal login flow - use the login function from useAuth
        await login({
          ...formData,
          email: normalizedEmail
        })
        
        console.log('[Login][Password] Backend login successful', { email: normalizedEmail })
        
        // Check if Azure is the existing provider and we need to link password
        // This handles the reverse case: Azure exists, user logs in with password
        try {
          const { fetchSignInMethodsForEmail, linkWithCredential, EmailAuthProvider, getAuth } = await import('firebase/auth')
          const auth = getAuth()
          if (tenantId) {
            auth.tenantId = tenantId
          }
          
          const existingMethods = await fetchSignInMethodsForEmail(auth, normalizedEmail)
          console.log('[Login][Password] Existing Firebase sign-in methods:', existingMethods)
          
          // Check if Azure is the existing provider but password is not linked
          const hasAzure = existingMethods.includes('oidc.myazure')
          const hasPassword = existingMethods.includes('password')
          
          if (hasAzure && !hasPassword) {
            console.log('[Login][Password] Azure is existing provider - password login successful, will link password on next Azure login')
            
            // Store flag to link password after Azure login
            // When user logs in with Azure next time, we'll link password to Azure account
            sessionStorage.setItem('link_password_after_azure', 'true')
            sessionStorage.setItem('link_password_email', normalizedEmail)
            sessionStorage.setItem('link_password_tenant_id', tenantId || '')
            
            toast('Your account uses Azure SSO. Password will be linked automatically on your next Azure login.', { duration: 6000 })
          }
        } catch (linkCheckError) {
          // Ignore errors - this is just a check, don't block login
          console.log('[Login][Password] Could not check existing providers:', linkCheckError.message)
        }
        
        // Check if we need to link Azure SSO after password login
        const linkAzureFlag = sessionStorage.getItem('link_azure_after_password')
        const linkAzureEmail = sessionStorage.getItem('link_azure_email')
        const ssoConfigStr = sessionStorage.getItem('sso_config')
        
        if (linkAzureFlag === 'true' && linkAzureEmail && normalizeEmail(linkAzureEmail) === normalizedEmail && ssoConfigStr) {
          console.log('[Login][Password] Azure linking flag detected - signing in Firebase with password, then linking Azure')
          try {
            const ssoConfig = JSON.parse(ssoConfigStr)
            
            // CRITICAL FIX: Use tenant ID from SSO config, not component state
            const ssoTenantId = ssoConfig.tenantId || tenantId
            if (!ssoTenantId) {
              throw new Error('No tenant ID in SSO config')
            }
            
            // First, sign in Firebase with password so we can link Azure
            const { signInWithEmailAndPassword, linkWithPopup, linkWithRedirect, linkWithCredential, fetchSignInMethodsForEmail, OAuthProvider, getAuth } = await import('firebase/auth')
            const auth = getAuth()
            auth.tenantId = ssoTenantId  // ✅ FIX: Use tenant from SSO config
            
            console.log('[Login][Password] Signing in Firebase with password for Azure linking', { tenantId: ssoTenantId })
            const firebaseUserCredential = await signInWithEmailAndPassword(auth, normalizedEmail, formData.password)
            console.log('[Login][Password] Firebase signed in successfully, now attempting Azure linking via popup')
            
            // CRITICAL FIX: Use linkWithPopup instead of signInWithPopup for linking
            // linkWithPopup is designed for linking accounts when already signed in
            const providerId = ssoConfig.provider === 'azure' || ssoConfig.provider === 'microsoft' ? 'oidc.myazure' : 'oidc.generic'
            const provider = new OAuthProvider(providerId)
            
            // Set custom parameters for Azure
            if (ssoConfig.provider === 'azure' || ssoConfig.provider === 'microsoft') {
              provider.setCustomParameters({
                prompt: 'select_account',
                login_hint: normalizedEmail
              })
            }
            
            // Try linkWithPopup first (designed for linking when already signed in)
            try {
              console.log('[Login][Password] Attempting Azure SSO linking via linkWithPopup')
              console.log('[Login][Password] Current user:', {
                email: firebaseUserCredential.user.email,
                uid: firebaseUserCredential.user.uid,
                tenantId: auth.tenantId
              })
              
              const popupResult = await linkWithPopup(firebaseUserCredential.user, provider)
              
              // If linking succeeds, Azure is now linked
              console.log('[Login][Password] ✅ Azure linking succeeded via linkWithPopup')
              
              // Verify linking succeeded
              await new Promise(resolve => setTimeout(resolve, 500))
              const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail)
              console.log('[Login][Password] Sign-in methods after linking:', methods)
              
              if (methods.includes('oidc.myazure') || methods.includes(providerId)) {
                console.log('[Login][Password] ✅ Verified: Azure linked successfully')
                
                // Get updated ID token and complete login
                const idToken = await popupResult.user.getIdToken()
                const response = await authAPI.login({ email: normalizedEmail, idToken })
                
                if (response.data.success && response.data.data) {
                  const { user, token } = response.data.data
                  localStorage.setItem('token', token)
                  toast.success('Azure SSO linked successfully! You can now use either password or Azure SSO to login.', { duration: 6000 })
                  
                  // Clear flags
                  sessionStorage.removeItem('link_azure_after_password')
                  sessionStorage.removeItem('link_azure_email')
                  sessionStorage.removeItem('sso_config')
                  
                  navigate('/dashboard', { replace: true })
                  return
                }
              } else {
                console.warn('[Login][Password] ⚠️ Linking completed but Azure provider not found in methods')
                toast.warning('Azure linking may not have completed. Please try again later.', { duration: 5000 })
              }
            } catch (popupError) {
              console.log('[Login][Password] Popup error details:', {
                code: popupError.code,
                message: popupError.message,
                hasCredential: !!popupError.credential,
                email: popupError.email
              })
              
              // Handle popup closed by user or blocked
              if (popupError.code === 'auth/popup-closed-by-user') {
                console.log('[Login][Password] Azure SSO popup was closed - could be user action or browser security')
                console.log('[Login][Password] Checking if popup was blocked by browser or if Azure is already linked...')
                
                // Check if Azure is already linked (might have been linked before)
                try {
                  const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail)
                  if (methods.includes('oidc.myazure') || methods.includes(providerId)) {
                    console.log('[Login][Password] ✅ Azure is already linked despite popup error')
                    toast.success('Azure SSO is already linked to your account.', { duration: 5000 })
                  } else {
                    toast('Azure SSO popup was closed. If you didn\'t close it, your browser may have blocked it. You can link Azure SSO later from your account settings.', { duration: 7000 })
                  }
                } catch (checkError) {
                  console.error('[Login][Password] Error checking sign-in methods:', checkError)
                  toast('Azure SSO popup was closed. You can link Azure SSO later from your account settings.', { duration: 5000 })
                }
                
                // Clear flags but don't block login
                sessionStorage.removeItem('link_azure_after_password')
                sessionStorage.removeItem('link_azure_email')
                sessionStorage.removeItem('sso_config')
                
                // User is already logged in, just continue to dashboard
                navigate('/dashboard', { replace: true })
                return
              }
              
              // Handle popup blocked
              if (popupError.code === 'auth/popup-blocked') {
                console.log('[Login][Password] Azure SSO popup was blocked by browser')
                toast.warning('Azure SSO popup was blocked by your browser. Please allow popups for this site, then sign out and sign in again to complete Azure linking.', { duration: 8000 })
                // Don't clear flags so user can retry
                navigate('/dashboard', { replace: true })
                return
              }
              
              // If popup fails with account-exists, try linking
              if (popupError.code === 'auth/account-exists-with-different-credential') {
                console.log('[Login][Password] 🔗 Account exists with different credential - attempting to link')
                console.log('[Login][Password] Before linking - User email:', firebaseUserCredential.user.email, 'UID:', firebaseUserCredential.user.uid)
                
                // Check current sign-in methods
                const methodsBefore = await fetchSignInMethodsForEmail(auth, normalizedEmail)
                console.log('[Login][Password] Methods BEFORE linking:', methodsBefore)
                
                // Check if Azure is already linked
                const hasAzureLinked = methodsBefore.includes('oidc.myazure') || methodsBefore.includes(providerId)
                if (hasAzureLinked) {
                  console.log('[Login][Password] ✅ Azure is already linked! No need to link again.')
                  toast.success('Azure SSO is already linked to your account. You can use either password or Azure SSO to login.', { duration: 5000 })
                  
                  // Clear flags and continue
                  sessionStorage.removeItem('link_azure_after_password')
                  sessionStorage.removeItem('link_azure_email')
                  sessionStorage.removeItem('sso_config')
                  
                  navigate('/dashboard', { replace: true })
                  return
                }
                
                // If we have a credential, try linking it
                if (popupError.credential) {
                  try {
                    console.log('[Login][Password] Attempting to link Azure credential...')
                    await linkWithCredential(firebaseUserCredential.user, popupError.credential)
                    console.log('[Login][Password] ✅ linkWithCredential succeeded')
                    
                    // Verify linking succeeded
                    await new Promise(resolve => setTimeout(resolve, 500))
                    const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail)
                    console.log('[Login][Password] Methods AFTER linking:', methods)
                    
                    if (methods.includes('oidc.myazure') || methods.includes(providerId)) {
                      console.log('[Login][Password] ✅ Verified: Azure linked successfully')
                      
                      // Get updated ID token and complete login
                      const idToken = await firebaseUserCredential.user.getIdToken()
                      const response = await authAPI.login({ email: normalizedEmail, idToken })
                      
                      if (response.data.success && response.data.data) {
                        const { user, token } = response.data.data
                        localStorage.setItem('token', token)
                        toast.success('Azure SSO linked successfully! You can now use either password or Azure SSO to login.', { duration: 6000 })
                        
                        // Clear flags
                        sessionStorage.removeItem('link_azure_after_password')
                        sessionStorage.removeItem('link_azure_email')
                        sessionStorage.removeItem('sso_config')
                        
                        navigate('/dashboard', { replace: true })
                        return
                      }
                    } else {
                      throw new Error('Linking verification failed - Azure provider not found after linking')
                    }
                  } catch (linkError) {
                    console.error('[Login][Password] ❌ linkWithCredential FAILED:', {
                      code: linkError.code,
                      message: linkError.message
                    })
                    
                    // If linking fails, check if Azure is already linked (might have been linked by another process)
                    const methodsAfterError = await fetchSignInMethodsForEmail(auth, normalizedEmail)
                    if (methodsAfterError.includes('oidc.myazure') || methodsAfterError.includes(providerId)) {
                      console.log('[Login][Password] ✅ Azure is already linked despite link error')
                      toast.success('Azure SSO is already linked to your account.', { duration: 5000 })
                      
                      sessionStorage.removeItem('link_azure_after_password')
                      sessionStorage.removeItem('link_azure_email')
                      sessionStorage.removeItem('sso_config')
                      
                      navigate('/dashboard', { replace: true })
                      return
                    }
                    
                    // If we can't link, inform user but don't block login
                    toast.warning('Could not automatically link Azure SSO. You can link it later from your account settings.', { duration: 6000 })
                    sessionStorage.removeItem('link_azure_after_password')
                    sessionStorage.removeItem('link_azure_email')
                    sessionStorage.removeItem('sso_config')
                    
                    navigate('/dashboard', { replace: true })
                    return
                  }
                } else {
                  // No credential provided - Azure account exists but we can't link it automatically
                  console.warn('[Login][Password] Account exists but no credential provided for linking')
                  
                  // Check if Azure is already linked anyway
                  const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail)
                  if (methods.includes('oidc.myazure') || methods.includes(providerId)) {
                    console.log('[Login][Password] ✅ Azure is already linked')
                    toast.success('Azure SSO is already linked to your account.', { duration: 5000 })
                  } else {
                    toast.warning('Azure account exists but could not be automatically linked. You can link it later from your account settings.', { duration: 6000 })
                  }
                  
                  sessionStorage.removeItem('link_azure_after_password')
                  sessionStorage.removeItem('link_azure_email')
                  sessionStorage.removeItem('sso_config')
                  
                  navigate('/dashboard', { replace: true })
                  return
                }
              } else {
                throw popupError
              }
            }
          } catch (linkError) {
            console.error('[Login][Password] Failed to link Azure:', linkError)
            // Better error handling
            if (linkError.code === 'auth/wrong-password' || linkError.code === 'auth/invalid-credential') {
              toast.error('Password authentication failed. Azure linking skipped.')
              sessionStorage.removeItem('link_azure_after_password')
              sessionStorage.removeItem('link_azure_email')
            } else if (linkError.code === 'auth/popup-blocked') {
              // Do NOT clear flags so user can retry after allowing popups (sign out → sign in with password again)
              toast.error(
                'Popup was blocked. Please allow popups for this site, then sign out and sign in with your password again to complete Azure linking.',
                { duration: 8000 }
              )
            } else if (linkError.code === 'auth/popup-closed-by-user') {
              toast.error('Azure SSO popup was closed. Sign in with password again to retry linking.')
              // Don't clear flags so they can retry
            } else {
              toast.error(`Failed to link Azure SSO: ${linkError.message || 'Unknown error'}. You can link it later.`)
              sessionStorage.removeItem('link_azure_after_password')
              sessionStorage.removeItem('link_azure_email')
            }
            // Continue to dashboard
          }
        }
        
        navigate('/dashboard', { replace: true })
      } else {
        // Handle registration
        console.log('Registration not implemented yet')
      }
    } catch (error) {
      console.error('[Login][Password] Auth error', error)
      const errorMessage = error.response?.data?.message || error.message || 'Invalid email or password'
      setLoginError(errorMessage)
    }
  }

  const handleForgotPassword = (e) => {
    e.preventDefault()
    navigate('/forgot-password', { state: { email: formData.email } })
  }

  const handleMFASuccess = async (data) => {
    const { user, token } = data
    
    // Update auth context with user and token
    if (completeMFALogin) {
      await completeMFALogin(user, token)
    } else {
      // Fallback: store token and let auth check handle it
      localStorage.setItem('token', token)
    }
    
    // Navigate to dashboard
    navigate('/dashboard', { replace: true })
  }

  const handleMFACancel = () => {
    setRequiresMFA(false)
    setMfaSessionToken(null)
    setStep('email')
    setFormData(prev => ({ ...prev, password: '' }))
  }

  const handleBack = () => {
    setLoginError(null)
    if (step === 'tenant-selection') {
      setStep('email')
      setAvailableTenants([])
      setTenantId(null)
      setTenantAutoDetected(false)
    } else {
      setStep('email')
      setFormData(prev => ({ ...prev, password: '' }))
      setSsoConfig(null)
      setTenantId(null)
      setTenantAutoDetected(false)
    }
  }

  const appName = branding?.app_name || 'Product Hub'
  const isBusy = step === 'email' ? (fetchingTenant || checkingSSO) : loading
  const submitLabel =
    step === 'email'
      ? fetchingTenant
        ? 'Looking up workspace...'
        : checkingSSO
          ? 'Checking sign-in...'
          : 'Continue'
      : 'Sign in'

  const emailChip = (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-secondary-200 bg-white px-3 py-2.5 shadow-sm">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <Mail className="h-4 w-4" />
        </span>
        <span className="truncate text-sm font-medium text-secondary-800">{formData.email}</span>
      </div>
      <button type="button" onClick={handleBack} className="auth-link shrink-0">
        Change
      </button>
    </div>
  )

  if (requiresMFA && mfaSessionToken) {
    return (
      <AuthShell
        title="Verify it's you"
        subtitle="Enter the code from your authenticator app to finish signing in."
      >
        <MFAVerification
          sessionToken={mfaSessionToken}
          onSuccess={handleMFASuccess}
          onCancel={handleMFACancel}
          embedded
        />
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title={step === 'tenant-selection' ? 'Choose a workspace' : `Sign in to ${appName}`}
      subtitle={
        step === 'tenant-selection'
          ? 'This email is linked to more than one tenant. Select the one you want to use.'
          : step === 'password'
            ? 'Enter your password to continue.'
            : 'Use your work email. We will route you to SSO when it is available.'
      }
      footer={
        <p className="text-center text-xs text-secondary-400">
          Access is by invitation. Contact your administrator if you need an account.
        </p>
      }
    >
      {step === 'tenant-selection' ? (
        <div className="space-y-4">
          {emailChip}
          <div className="space-y-2.5">
            {availableTenants.map((tenant, index) => (
              <button
                key={tenant.tenantId || index}
                type="button"
                onClick={() => handleTenantSelect(tenant.tenantId)}
                className="group flex w-full items-center justify-between rounded-xl border border-secondary-200 bg-white p-4 text-left shadow-sm transition hover:border-primary-400 hover:bg-primary-50/40"
              >
                <div className="min-w-0">
                  <div className="font-medium text-secondary-900">
                    {tenant.displayName || 'Default workspace'}
                  </div>
                  {tenant.tenantId && (
                    <div className="mt-0.5 truncate text-xs text-secondary-400">{tenant.tenantId}</div>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-secondary-300 transition group-hover:text-primary-600" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (step === 'email') {
              handleContinue(e)
            } else if (step === 'password') {
              handleSubmit(e)
            } else {
              console.warn('[Login] Form submitted but step is not email or password:', step)
            }
          }}
          className="space-y-5"
        >
          {step === 'password' ? (
            <>
              <input
                type="email"
                name="email"
                value={formData.email}
                readOnly
                autoComplete="username"
                className="sr-only"
                tabIndex={-1}
              />
              {emailChip}
            </>
          ) : (
            <div>
              <label htmlFor="email" className="auth-field">
                Email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="auth-input w-full pl-11"
                  disabled={fetchingTenant}
                />
              </div>
            </div>
          )}

          {step === 'password' && (
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-secondary-700">
                  Password
                </label>
                <button type="button" onClick={handleForgotPassword} className="auth-link text-xs">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoFocus
                  required
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`auth-input w-full pl-11 pr-11 ${loginError ? 'border-error-500' : ''}`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-secondary-400 hover:text-secondary-600"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {loginError && (
                <p className="mt-2 flex items-start gap-1.5 text-sm text-error-600">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {loginError}
                </p>
              )}
            </div>
          )}

          {(error || loginError) && step !== 'password' && (
            <div className="flex items-start gap-2 rounded-xl border border-error-200 bg-error-50 px-3 py-2.5">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-error-500" />
              <p className="text-sm text-error-600">{error || loginError}</p>
            </div>
          )}

          <Button
            type="submit"
            className="h-12 w-full rounded-xl text-[15px] font-semibold shadow-sm"
            loading={isBusy}
            disabled={isBusy}
          >
            {submitLabel}
            {!isBusy && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
